import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiSearch, FiX, FiFileText, FiCalendar, FiFilter, FiUser, FiPhone, FiPackage, FiDollarSign } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { orderAPI, productAPI } from '../../services/api';

const statuses = ['pending', 'processing', 'completed', 'cancel'];

const OrderManagement = () => {
  const { isSubAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState({});
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newLivre, setNewLivre] = useState('');
  
  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchOrders();
    fetchAllProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, selectedStatus, filterType, filterDate, startDate, endDate, orders]);

  const navigate = useNavigate();

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await orderAPI.getAll();
      console.log('📦 Orders API response sur les donnees:', response.data);
      
      if (response.data.success && response.data.data) {
        console.log('donnees charger avec succes', response.data.data)
        setOrders(response.data.data.data);
      } else if (Array.isArray(response.data)) {
        setOrders(response.data);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Récupérer tous les produits
  const fetchAllProducts = async () => {
    try {
      const response = await productAPI.getAll();
      console.log('📦 Tous les produits:', response.data);
      
      // Créer un objet {id: nom} pour accès rapide
      const productsMap = {};
      if (response.success && response.data.data) {
        response.data.data.forEach(product => {
          productsMap[product.id] = product.name || 'Produit inconnu';
        });
      }
      setProducts(productsMap);
    } catch (error) {
      console.error('Erreur chargement produits:', error);
    }
  };

  const applyFilters = () => {
    let result = orders;

    // Filtre par recherche (nom utilisateur ou téléphone)
    if (searchTerm) {
      result = result.filter(order => {
        const customerName = order.recipient_name || '';
        const customerPhone = order.recipient_phone || '';
        return (
          customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customerPhone.includes(searchTerm)
        );
      });
    }

    // Filtre par statut
    if (selectedStatus) {
      result = result.filter(order => order.status === selectedStatus);
    }

    // Filtre par date spécifique
    if (filterType === 'date' && filterDate) {
      const selectedDate = new Date(filterDate).toDateString();
      result = result.filter(order => {
        const orderDate = new Date(order.created_at).toDateString();
        return orderDate === selectedDate;
      });
    }

    // Filtre par intervalle de date
    if (filterType === 'range' && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      result = result.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate >= start && orderDate <= end;
      });
    }

    setFilteredOrders(result);
  };

  const handleStatusAndAmountChange = async () => {
    if (!newStatus) {
      alert('Veuillez sélectionner un statut');
      return;
    }

    // Validation du montant
    if (!newAmount || isNaN(parseFloat(newAmount))) {
      alert('Veuillez saisir un montant valide');
      return;
    }

    if (!newLivre || isNaN(parseFloat(newLivre))) {
      alert('Veuillez saisir un montant valide');
      return;
    }

    try {
      const orderId = editingOrder.id;
      
      // Convertir le montant en float avec 2 décimales
      const formattedAmount = parseFloat(newAmount).toFixed(2);
      
      // Préparer les données pour l'API
      const updateData = {
        status: newStatus,
        total_amount: formattedAmount
      };
      
      console.log('📤 Envoi des données de mise à jour:', updateData);
      
      // Appeler l'API pour mettre à jour statut et montant
      await orderAPI.updateStatMont(orderId, updateData);
      
      console.log('✅ Statut et montant modifiés avec succès!');
      console.log('✅ Commande mise à jour avec succès!');
      
      // Recharger les commandes
      fetchOrders();
      closeModal();
    } catch (error) {
      console.error('Erreur:', error);
      alert('❌ Erreur lors de la modification: ' + (error.response?.data?.error || error.message || error));
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setNewStatus(order.status);
    
    // Formater le montant actuel pour l'input
    const currentAmount = order.total_amount || 0;

    //Formater le montant de la livraison 
    // const livre = order.Livre

    // Convertir en nombre et formater sans espace ni séparateur de milliers
    const amountStr = currentAmount.toString().replace(/\s+/g, '').replace(',', '.');
    const amountNum = parseFloat(amountStr);
    // Afficher sans décimales si c'est un entier
    setNewAmount(isNaN(amountNum) ? '0' : amountNum % 1 === 0 ? amountNum.toString() : amountNum.toFixed(2));
    // setNewLivre('5000');
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette commande?')) {
      try {
        await orderAPI.deleteorders(id);
        alert('✅ Commande supprimée avec succès!');
        fetchOrders();
      } catch (error) {
        console.error('Erreur:', error);
        alert('❌ Erreur lors de la suppression: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingOrder(null);
    setNewStatus('');
    setNewAmount('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-orange-100 text-orange-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancel':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'pending': return 'En attente';
      case 'processing': return 'Arnaquer';
      case 'completed': return 'Livrée';
      case 'cancel': return 'Annulée';
      default: return status;
    }
  };

  // Fonction pour obtenir le nom du client
  const getCustomerName = (order) => {
    return order.recipient_name || 'Client';
  };

  // Fonction pour obtenir le téléphone
  const getCustomerPhone = (order) => {
    return order.recipient_phone || 'N/A';
  };

  // Fonction pour obtenir le statut
  const getOrderStatus = (order) => {
    return order.status || 'Inconnu';
  };

  // Fonction pour obtenir le montant total
  const getTotalAmount = (order) => {
    const total = order.total_amount || 0;
    // Formatage pour l'affichage
    const amountStr = total.toString().replace(/\s+/g, '').replace(',', '.');
    const amountNum = parseFloat(amountStr);
    return (isNaN(amountNum) ? 0 : amountNum).toLocaleString('fr-FR') + ' FCFA';
  };

  // Fonction pour obtenir le montant brut (sans formatage)
  const getRawAmount = (order) => {
    const total = order.total_amount || 0;
    const amountStr = total.toString().replace(/\s+/g, '').replace(',', '.');
    const amountNum = parseFloat(amountStr);
    return isNaN(amountNum) ? 0 : amountNum;
  };

  // Fonction pour obtenir la date
  const getOrderDate = (order) => {
    const date = order.created_at;
    return date ? new Date(date).toLocaleDateString('fr-FR') : 'Date inconnue';
  };

  // Fonction pour obtenir l'ID
  const getOrderId = (order) => {
    return order.id;
  };

  // Fonction pour obtenir les produits de la commande
  const getOrderProducts = (order) => {
    if (order.items && Array.isArray(order.items)) {
      return order.items;
    }
    return [];
  };

  // Fonction pour obtenir le nom d'un produit (synchrone maintenant)
  const getProductName = (productId) => {
    if (!productId) return 'Produit';
    return products[productId] || 'Produit';
  };

  // Fonction pour valider et formater le montant
  const handleAmountChange = (value) => {
    // Remplacer les virgules par des points
    const cleanedValue = value.replace(',', '.');
    
    // Vérifier que c'est un nombre valide
    if (cleanedValue === '' || /^\d*\.?\d*$/.test(cleanedValue)) {
      setNewAmount(cleanedValue);
    }
  };

  const handleLivreChange = (value) => {
    // Remplacer les virgules par des points
    const cleanedValue = value.replace(',', '.');
    
    // Vérifier que c'est un nombre valide
    if (cleanedValue === '' || /^\d*\.?\d*$/.test(cleanedValue)) {
      setNewLivre(cleanedValue);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Gestion des Commandes</h1>
        <p className="text-gray-600">Total: {filteredOrders.length} commande{filteredOrders.length > 1 ? 's' : ''}</p>
        {isSubAdmin && (
          <p className="text-sm text-blue-600 mt-2">
            ℹ️ Vous pouvez consulter et supprimer les commandes, mais pas modifier le statut
          </p>
        )}
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6 overflow-x-auto">
        <div className="flex items-center gap-2 mb-4">
          <FiFilter className="text-orange-600 text-xl flex-shrink-0" />
          <h2 className="font-semibold text-gray-800">Filtres</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {/* Recherche par nom */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Recherche
            </label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Nom ou téléphone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Filtre par statut */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Statut
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
            >
              <option value="">Tous</option>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status === 'processing' ? 'Arnaquer' : 
                  status === 'completed' ? 'Livrée' : 
                  status === 'cancel' ? 'Annulée' : 
                  status === 'pending' ? 'En attente' : status}
                </option>
              ))}
            </select>
          </div>

          {/* Type de filtre date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Type de filtre
            </label>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setFilterDate('');
                setStartDate('');
                setEndDate('');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
            >
              <option value="all">Aucun</option>
              <option value="date">Date</option>
              <option value="range">Intervalle</option>
            </select>
          </div>

          {/* Filtre date spécifique */}
          {filterType === 'date' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Date
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Intervalle de dates */}
        {filterType === 'range' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Début
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fin
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tableau des commandes */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">ID</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Client</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Produits</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Montant</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Statut</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Date</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order) => (
                <tr key={getOrderId(order)} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-xs text-gray-600 font-mono whitespace-nowrap">
                    #{getOrderId(order).toString().substring(0, 8)}...
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="bg-blue-100 p-2 rounded-full">
                        <FiUser className="text-blue-600 text-sm" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{getCustomerName(order)}</p>
                        <p className="text-xs text-gray-600 flex items-center gap-1">
                          <FiPhone className="text-xs" />
                          {getCustomerPhone(order)}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs space-y-1">
                      {getOrderProducts(order).length > 0 ? (
                        <>
                          {getOrderProducts(order).slice(0, 2).map((item, idx) => (
                            <div key={idx} className="flex items-center gap-1">
                              <FiPackage className="text-gray-400 text-xs" />
                              <span className="text-gray-700">
                                {getProductName(item.product_id) || 'produit' } x{item.quantity || 1}
                              </span>
                            </div>
                          ))}
                          {getOrderProducts(order).length > 2 && (
                            <p className="text-gray-500 font-semibold">+{getOrderProducts(order).length - 2} autre(s)</p>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-500 italic">Aucun détail produit</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-orange-600 whitespace-nowrap">
                    {getTotalAmount(order)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(getOrderStatus(order))}`}>
                      {getStatusLabel(getOrderStatus(order))}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                    {getOrderDate(order)}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                    {getStatusLabel(getOrderStatus(order)) === 'Livrée' && (
                      // Bouton pour voir la facture quand la commande est livrée
                      <button
                        onClick={() => navigate(`/dashboard/invoice/${getOrderId(order)}?newLivre=${encodeURIComponent(newLivre)}`)}
                        className="text-yellow-600 hover:text-yellow-800 inline-block p-2 hover:bg-yellow-50 rounded-lg transition-all duration-200"
                        title="Voir la facture"
                      >
                        <FiFileText className="text-lg" />
                      </button>
                    )}
                    {/* // Boutons Modifier/Supprimer pour les autres statuts */}
                    <button
                      onClick={() => handleEdit(order)}
                      className="text-blue-600 hover:text-blue-900 inline-block p-2 hover:bg-blue-50 rounded-lg transition-all duration-200"
                      title="Modifier la commande"
                    >
                      <FiEdit2 className="text-lg" />
                    </button>
                    {!isSubAdmin && (
                      <button
                        onClick={() => handleDelete(getOrderId(order))}
                        className="text-red-600 hover:text-red-900 inline-block p-2 hover:bg-red-50 rounded-lg transition-all duration-200"
                        title="Supprimer"
                      >
                        <FiTrash2 className="text-lg" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <FiPackage className="text-4xl text-gray-300 mb-2" />
                    <p>Aucune commande trouvée</p>
                    <button 
                      onClick={fetchOrders}
                      className="mt-2 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm"
                    >
                      Actualiser
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de modification du statut et du montant - SEULEMENT pour admin principal */}
      {!isSubAdmin && isModalOpen && editingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-gray-800">Modifier la commande</h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-full">
                <FiX className="text-2xl text-gray-600" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations de la commande */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-3">Informations de la commande</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Client:</span>
                    <span className="font-semibold">{getCustomerName(editingOrder)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Téléphone:</span>
                    <span className="font-semibold">{getCustomerPhone(editingOrder)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-semibold">{getOrderDate(editingOrder)}</span>
                  </div>
                </div>
              </div>

              {/* Champ pour modifier le montant */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nouveau montant (FCFA) *
                </label>
                <div className="relative">
                  <FiDollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={newAmount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    placeholder="Ex: 1500 ou 1499.99"
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Saisissez le montant (ex: 1500 pour 1500.00 FCFA)
                </p>
                <div className="mt-2 p-2 bg-blue-50 rounded">
                  <p className="text-xs text-blue-700">
                    <strong>Montant actuel:</strong> {getTotalAmount(editingOrder)}
                  </p>
                </div>
              </div>

              {/* Champ pour modifier le statut */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nouveau statut *
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Sélectionner un statut</option>
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {status === 'processing' ? 'Arnaquer' : 
                      status === 'completed' ? 'Livrée' : 
                      status === 'cancel' ? 'Annulée' : 
                      status === 'pending' ? 'En attente' : status}
                    </option>
                  ))}
                </select>
                <div className="mt-2 p-2 bg-gray-50 rounded">
                  <p className="text-xs text-gray-700">
                    <strong>Statut actuel:</strong> 
                    <span className={`ml-2 px-2 py-1 rounded-full ${getStatusColor(getOrderStatus(editingOrder))}`}>
                      {getStatusLabel(getOrderStatus(editingOrder))}
                    </span>
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Montant de la livraison (FCFA) *
                </label>
                <div className="relative">
                  <FiDollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={newLivre}
                    onChange={(e) => handleLivreChange(e.target.value)}
                    placeholder="Ex: 1500 ou 1499.99"
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex gap-4 pt-4 border-t">
                <button
                  onClick={closeModal}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleStatusAndAmountChange}
                  disabled={!newStatus || !newAmount}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    !newStatus || !newAmount || !newLivre
                      ? 'bg-gray-400 text-gray-500 cursor-not-allowed'
                      : 'bg-yellow-500 text-white hover:bg-orange-700'
                  }`}
                >
                  Enregistrer les modifications
                </button>
              </div>

              {/* Note sur le formatage */}
              <div className="text-xs text-gray-500 italic">
                <p>Le montant sera automatiquement formaté avec 2 décimales (ex: 1500 → 1500.00)</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;